import cn from 'classnames';
import { CSSProperties, useState } from 'react';
import { ColumnFiltersState, flexRender } from '@tanstack/react-table';
import { useLegacyTable } from '@tanstack/react-table/legacy';
import {
  ColumnGroupToggleRow,
  getHierarchicalRowClassName,
  getHierarchicalTableDefaults,
  useColumnGroupToggle
} from '../HierarchicalTable';
import { TableRow } from './post-optimization-constants';
import { postOptimizationConfig } from './post-optimization-config';
import { PostOptimizationEditState } from './usePostOptimizationEdits';
import { usePostOptimizationColumns } from './usePostOptimizationColumns';

/** Column toggle group configuration */
const COLUMN_TOGGLE_CONFIG = {
  /** Columns that can be toggled as a group */
  columns: [
    'lastLive',
    'allocationMetric',
    'mustInclude',
    'mustExclude',
    'finalAllocation',
    'lineItemCpm'
  ],
  /** Column where the toggle button appears */
  buttonColumn: 'placement',
  /** Last column in the group (bracket ends here) */
  lastColumn: 'lineItemCpm'
};

/** Column presentation styles */
interface ColumnStyle {
  fixedWidth?: boolean;
  truncate?: boolean;
  maxWidth?: number;
}

const COLUMN_STYLES: Record<string, ColumnStyle> = {
  expander: { fixedWidth: true },
  networkContent: { fixedWidth: true, truncate: true, maxWidth: 150 },
  format: { fixedWidth: true },
  exploitExplore: { fixedWidth: true },
  placement: { fixedWidth: true, truncate: true, maxWidth: 250 }
};

/** Get CSS class names for a column */
const getColumnClassName = (columnId: string): string => {
  const style = COLUMN_STYLES[columnId];
  if (!style) {
    return '';
  }
  return cn({
    'fixed-width': Boolean(style.fixedWidth),
    truncatable: Boolean(style.truncate),
    'expander-cell': columnId === 'expander'
  });
};

/** Get inline style for a column (for dynamic max-width) */
const getColumnStyle = (columnId: string): CSSProperties | undefined => {
  const maxWidth = COLUMN_STYLES[columnId]?.maxWidth;
  return maxWidth ? { maxWidth } : undefined;
};

interface PostOptimizationTableProps {
  data: TableRow[];
  onUpdateEdit: (row: TableRow, edits: PostOptimizationEditState) => void;
}

export const PostOptimizationTable = ({ data, onUpdateEdit }: PostOptimizationTableProps) => {
  const columns = usePostOptimizationColumns(onUpdateEdit);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const columnGroupToggle = useColumnGroupToggle({
    columnIds: COLUMN_TOGGLE_CONFIG.columns,
    initialVisible: true
  });

  const table = useLegacyTable({
    data,
    columns,
    ...getHierarchicalTableDefaults(postOptimizationConfig),
    state: {
      columnFilters,
      columnVisibility: columnGroupToggle.columnVisibility
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: columnGroupToggle.setColumnVisibility
  });

  return (
    <div className='post-optimization-table'>
      <table aria-label='Post-optimization allocations'>
        <thead>
          <ColumnGroupToggleRow
            headers={table.getHeaderGroups()[0]?.headers ?? []}
            buttonColumn={COLUMN_TOGGLE_CONFIG.buttonColumn}
            lastColumn={COLUMN_TOGGLE_CONFIG.lastColumn}
            toggle={columnGroupToggle}
            hideTooltip='Hide placement columns'
            showTooltip='Show placement columns'
            getHeaderClassName={getColumnClassName}
          />
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className={getColumnClassName(header.id)}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={getHierarchicalRowClassName(row.original, postOptimizationConfig)}
            >
              {row.getVisibleCells().map(cell => {
                const columnId = cell.column.id;

                // Show the full value on hover for truncatable cells
                const rawValue = cell.getValue();
                const title =
                  COLUMN_STYLES[columnId]?.truncate && rawValue != null && rawValue !== ''
                    ? String(rawValue)
                    : undefined;

                return (
                  <td
                    key={cell.id}
                    className={getColumnClassName(columnId)}
                    style={getColumnStyle(columnId)}
                    title={title}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
